import { useEffect, useRef, useState } from "react"
import {
  Butterfly,
  IconProps,
  InstagramLogo,
  LinkSimple,
  SnapchatLogo,
  TiktokLogo,
} from "phosphor-react"
import type { ShareUserProfileFragment } from "~/__generated__/graphql"
import { cn } from "~/common/cn"
import { formatCount } from "~/common/number-utils"
import { AppText } from "~/ui/app-text"
import { UserAvatar } from "~/ui/user-avatar"
import { displayWebUrl, safeWebUrl, SocialKind, socialLinks } from "./profile-links"

const XLogo = ({ size = 20 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)

const SOCIAL_ICONS: Record<SocialKind, (props: IconProps) => JSX.Element> = {
  instagram: (props) => <InstagramLogo weight="fill" {...props} />,
  x: XLogo,
  tiktok: (props) => <TiktokLogo weight="fill" {...props} />,
  snapchat: (props) => <SnapchatLogo weight="fill" {...props} />,
  bluesky: (props) => <Butterfly weight="fill" {...props} />,
}

const Count = ({ count, label }: { count: number; label: string }) => (
  <div className="flex flex-col">
    <AppText variant="hairline-2" className="text-base-black">
      {formatCount(count)}
    </AppText>
    <AppText variant="body3-medium" className="text-base-black/60">
      {label}
    </AppText>
  </div>
)

const Bio = ({ bio }: { bio: string }) => {
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setOverflows(el.scrollHeight - el.clientHeight > 1)
    measure()
    document.fonts?.ready.then(measure)
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [bio])

  return (
    <div className="flex flex-col items-start gap-1">
      <AppText
        ref={ref}
        variant="body2"
        className={cn(
          "whitespace-pre-line break-words text-base-black",
          !expanded && "line-clamp-3"
        )}
      >
        {bio.replace(/\n{3,}/g, "\n\n")}
      </AppText>
      {(overflows || expanded) && (
        <button type="button" onClick={() => setExpanded(!expanded)}>
          <AppText variant="body2-medium" className="text-base-black underline">
            {expanded ? "See less" : "See more"}
          </AppText>
        </button>
      )}
    </div>
  )
}

export const ProfileCard = ({ profile }: { profile: ShareUserProfileFragment }) => {
  const displayName = profile.displayName || profile.username
  const webUrl = safeWebUrl(profile.webUrl)
  const socials = socialLinks(profile)

  return (
    <div className="flex w-full flex-col gap-4 rounded-[24px] bg-neutral-100 p-5">
      <div className="flex flex-row items-center gap-4">
        <UserAvatar
          avatarUrl={profile.avatarUrl}
          displayName={displayName}
          verifiedAccount={profile.verifiedAccount}
          size="lg"
          imgClassName="border-base-black/10"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-w-0 flex-col">
            <AppText as="h1" variant="h3" className="truncate text-base-black">
              {displayName}
            </AppText>
            <AppText variant="body3" className="truncate text-base-black/60">
              @{profile.username}
            </AppText>
          </div>
          <div className="flex flex-row gap-6">
            <Count count={profile.activeFollowerCount} label="Followers" />
            <Count count={profile.activeFollowingCount} label="Following" />
          </div>
        </div>
      </div>

      {(profile.bio || webUrl || socials.length > 0) && (
        <div className="flex flex-col gap-4 px-1">
          {profile.bio && <Bio bio={profile.bio} />}
          {webUrl && (
            <a
              href={webUrl.href}
              target="_blank"
              rel="noopener noreferrer nofollow ugc"
              className="flex min-w-0 flex-row items-center gap-2 text-base-black"
            >
              <LinkSimple size={16} weight="bold" className="shrink-0" />
              <AppText variant="body2-medium" className="truncate underline">
                {displayWebUrl(webUrl)}
              </AppText>
            </a>
          )}
          {socials.length > 0 && (
            <div className="flex flex-row flex-wrap gap-2">
              {socials.map(({ kind, label, href }) => {
                const Icon = SOCIAL_ICONS[kind]
                return (
                  <a
                    key={kind}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer nofollow ugc"
                    aria-label={label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-base-black/10 text-base-black transition-colors hover:bg-base-black/5"
                  >
                    <Icon size={18} />
                  </a>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
